import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Genesis M6+ · Magic-link verify landing page.
 *
 * The magic-link emails (dispatched by `services/genesis/magic_link.py`)
 * point at `/brand-os/auth/magic-link?token=<opaque>&next=<optional>`.
 * This page:
 *
 *   1. Extracts the token from the query string.
 *   2. POSTs it to `/api/brand-os/genesis/auth/magic-link/verify`.
 *   3. Refreshes the auth context and navigates the user to their
 *      requested `next` destination (or the dashboard).
 *   4. Displays a resilient status card if anything fails — the token is
 *      single-use and short-lived, so the copy must guide the recipient
 *      to request a fresh link.
 */
export default function MagicLinkVerify() {
  const { refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState(
    "Signing you into Precipice Brand OS…"
  );

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(location.search);
    const token = (params.get("token") || "").trim();
    const next = (params.get("next") || "/brand-os").trim();

    if (!token) {
      setStatus("error");
      setMessage(
        "This link is missing its verification token. Ask for a new sign-in link and try again."
      );
      return;
    }

    (async () => {
      try {
        await api.post("/genesis/auth/magic-link/verify", { token });
        await refresh();
        setStatus("success");
        setMessage("Sign-in verified. Taking you to your workspace…");
        // Small delay so the confirmation is visible.
        setTimeout(() => navigate(next, { replace: true }), 700);
      } catch (err) {
        setStatus("error");
        setMessage(formatApiError(err));
      }
    })();
  }, [location.search, navigate, refresh]);

  return (
    <div className="bo-root" data-testid="magic-link-verify">
      <div className="bo-login-shell">
        <div
          className="bo-login-card"
          style={{ textAlign: "left" }}
          data-testid="magic-link-card"
        >
          <div className="bo-login-eyebrow" data-testid="magic-link-eyebrow">
            Precipice Brand OS · Sign-in
          </div>
          <h1
            className="bo-login-title"
            style={{ fontSize: 26 }}
            data-testid="magic-link-title"
          >
            {status === "verifying" && "One moment…"}
            {status === "success" && "You're in."}
            {status === "error" && "This link didn't work."}
          </h1>
          <p
            className="bo-login-lede"
            style={{ marginTop: 8 }}
            data-testid="magic-link-message"
          >
            {message}
          </p>

          {status === "verifying" && (
            <div
              style={{ marginTop: 24, textAlign: "center" }}
              data-testid="magic-link-spinner"
            >
              <div
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: "3px solid var(--bo-panel-alt, #e6e2d8)",
                  borderTopColor: "var(--bo-ink, #2b2c30)",
                  margin: "0 auto",
                  animation: "bo-spin 800ms linear infinite",
                }}
              />
              <style>{`@keyframes bo-spin { to { transform: rotate(360deg);} }`}</style>
            </div>
          )}

          {status === "error" && (
            <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
              <button
                type="button"
                className="bo-btn bo-btn-primary"
                onClick={() => navigate("/brand-os/login", { replace: true })}
                data-testid="magic-link-return-to-login"
              >
                Request a new link
              </button>
              <button
                type="button"
                className="bo-btn bo-btn--ghost"
                onClick={() => navigate("/brand-os", { replace: true })}
                data-testid="magic-link-home"
              >
                Go to workspace
              </button>
            </div>
          )}

          <div className="bo-login-footer" style={{ marginTop: 24 }}>
            Magic links expire 15 minutes after they are sent, and can only
            be used once.
          </div>
        </div>
      </div>
    </div>
  );
}
